<template>
  <component
    :is="wrapperComponent"
    class="app-button"
    :class="{
      'app-button--dark': props.dark,
      'app-button--light': props.light,
      'app-button--tertiary': props.tertiary,
      'app-button--secondary': props.secondary,
      'app-button--medium': props.medium,
      'app-button--small': props.small,
      'app-button--smaller': props.smaller,
      'app-button--error': props.error,
      'app-button--disabled': props.disabled,
      'app-button--loading': props.loading,
      'app-button--icon-right': props.iconRight,
    }"
    :type="type"
    :to="to"
    :disabled="nativeDisabled"
    :aria-disabled="isInert ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :tabindex="isInert && to ? -1 : undefined"
  >
    <AppSpinner
      v-if="loading"
      dimension="21px"
      :color="spinnerColor"
    />
    <slot v-if="showText" />
  </component>
</template>

<script setup>
  import NuxtLink from '#app/components/nuxt-link'

  const props = defineProps({
    tertiary: {
      default: false,
      type: Boolean,
    },
    secondary: {
      default: false,
      type: Boolean,
    },
    medium: {
      default: false,
      type: Boolean,
    },
    small: {
      default: false,
      type: Boolean,
    },
    smaller: {
      default: false,
      type: Boolean,
    },
    type: {
      type: String,
      default: 'button',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideTextOnLoading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    to: {
      type: String,
      default: null,
    },
    dark: {
      // In combination with primary
      type: Boolean,
      default: false,
    },
    light: {
      // In combination with secondary
      type: Boolean,
      default: false,
    },
    iconRight: {
      type: Boolean,
      default: false,
    },
  })

  const showText = computed(() => {
    if (!props.hideTextOnLoading) {
      return true
    }

    return !props.loading
  })

  const spinnerColor = computed(() => {
    if (props.secondary) {
      return 'black'
    }

    return 'white'
  })

  const wrapperComponent = computed(() => (props.to ? NuxtLink : 'button'))

  // `disabled` and `loading` used to be styling-only (`pointer-events: none`),
  // so a gated button stayed focusable, was announced as enabled, and still
  // fired on Enter. Bind the real attribute — but only on a native <button>,
  // since NuxtLink renders an <a>, where `disabled` is meaningless.
  const isInert = computed(() => props.disabled || props.loading)
  const nativeDisabled = computed(() => (!props.to && isInert.value ? true : undefined))
</script>

<style lang="scss">
  .app-button {
    @include appButton();
  }

  .app-button:has(> .icon) {
    padding: 8px 26px 8px 14px;
  }

  .app-button.app-button--icon-right:has(> .icon) {
    padding: 8px 14px 8px 26px;
  }

  .app-button:disabled {
    cursor: not-allowed;
  }

  // The app shipped no focus styling at all, so keyboard users had nothing to
  // track. Offset ring so it stays visible against both fills and outlines.
  .app-button:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  // Dark
  .app-button--dark {
    background: var(--color-primary);
    color: var(--color-secondary);

    &::after {
      background-color: #575d88;
    }

    &:hover {
      --button-text-color: var(--color-primary);
    }
  }

  // Secondary
  .app-button--secondary {
    --button-text-color: var(--color-primary);

    background: transparent;
    border: 1px solid var(--color-primary);
    transition: color 0.2s ease-in-out;

    &::after {
      background-color: var(--color-primary);
    }

    &:hover::after {
      @media not all and (pointer: coarse) {
        transform: translate(0, 0);
      }
    }

    &:hover {
      --button-text-color: var(--color-secondary);
    }
  }

  .app-button--secondary.app-button--light {
    border-color: var(--color-secondary);
    --button-text-color: var(--color-secondary);

    &::after {
      background-color: var(--color-secondary);
    }

    &:hover {
      --button-text-color: var(--color-primary);
    }
  }

  // Tertiary
  .app-button--tertiary {
    --button-text-color: var(--color-primary);
    background: transparent;
    border-color: transparent;
  }

  .app-button--medium {
    padding: 5px 26px;
    font-size: 16px;
    line-height: 26px;
  }

  .app-button--medium:has(> .icon) {
    padding: 5px 26px 5px 14px;
  }
  .app-button--medium.app-button--icon-right:has(> .icon) {
    padding: 5px 14px 5px 26px;
  }

  .app-button--small {
    padding: 4px 24px;
    font-size: 16px;
    line-height: 24px;
  }

  .app-button--small:has(> .icon) {
    padding: 4px 24px 4px 12px;
  }

  .app-button--small.app-button--icon-right:has(> .icon) {
    padding: 4px 12px 4px 24px;
  }

  .app-button--smaller {
    padding: 4px 16px;
    font-size: 16px;
    line-height: 24px;
  }

  .app-button--smaller:has(> .icon) {
    padding: 4px 16px 4px 8px;
  }

  .app-button--smaller.app-button--icon-right:has(> .icon) {
    padding: 4px 8px 4px 16px;
  }

  // Compact buttons: the button's flex gap (8px) already separates icon and
  // label — the global .icon-left utility's margin-right doubled that to
  // 16px, which next to the reduced 8px edge padding made icons look
  // detached from their label.
  .app-button--small > .icon-left,
  .app-button--smaller > .icon-left {
    margin-right: 0;
  }

  .app-button--error {
    color: white;
    background: var(--color-error);
    border: 1px solid var(--color-error);

    &::after {
      background-color: var(--color-error-light);
    }
  }

  .app-button--secondary.app-button--error {
    background: transparent;
    color: var(--color-error);
    border: 1px solid var(--color-error);
  }

  .app-button--secondary.app-button--error:hover {
    border: 1px solid var(--color-error);
  }

  .app-button--disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  .app-button--loading {
    pointer-events: none;
  }
</style>
